<template>
  <main>
    <div class="l-main-content__inner">

      <div class="c-page-header">
        <h1 class="c-page-header-title">学年別 学習状況</h1>
      </div>

        <!-- 学年 -->
        <dl class="p-learning-filtering -no-gap">
            <!-- 学年 -->
            <select-single
              v-if="conditions"
              title="学年"
              ref="academicYearsSelector"
              :options="academicYearsBelongCurrentAcademicSessionId"
              v-model:selected="academicYear"
              @change="changeAcademicYear"
            ></select-single>
          </dl>

        <section>

          <!-- 教科 -->
          <subject-tabs
            :subject="subject"
            @click="clickSubject"
          ></subject-tabs>

          <div class="c-parts-box -white -learning">

            <!-- 表示期間 -->
            <period-switches
              :period="period"
              @click="clickPeriod"
            ></period-switches>

            <!-- 教材 -->
            <learning-status-work-selector
              :academicSessionId="selectedAcademicSessionId"
              :academicYear="academicYear"
              :subject="subject"
              v-model:workId="workId"
              @change="changeWork"
            ></learning-status-work-selector>

            <!-- 学級状況へリンク -->
            <classroom-links
              v-if="conditions && classroomsBelongCurrentAcademicYear"
              :classrooms="classroomsBelongCurrentAcademicYear"
              :to="conditions.learningStatusClassBaseUrl"
              :extra-query="queryString"
            ></classroom-links>

            <!-- 学習状況グラフ -->
            <div class="p-learning-detail">
              <!-- 進捗率 -->
              <line-chart
                v-if="getProgressRateChartData"
                :chart-meta="progressRate"
                :chart-data="getProgressRateChartData"
              ></line-chart>
              <!-- 学習した問題数 -->
              <line-chart
                v-if="getStudiedQuestionsChartData"
                :chart-meta="studiedQuestions"
                :chart-data="getStudiedQuestionsChartData"
              ></line-chart>
              <!-- 学習時間 -->
              <line-chart
                v-if="getStudiedTimeChartData"
                :chart-meta="studiedTime"
                :chart-data="getStudiedTimeChartData"
                :period="period"
              ></line-chart>
            </div>

          </div>

        </section>

    </div>
    <error-modal></error-modal>
  </main>
</template>

<script setup lang="ts">
  import {ref, computed, onMounted} from "vue";
  import { SUBJECTS, SUBJECT_ALL } from "@/consts/subject";
  import { JUNIOR_HIGH_ACADEMIC_YEARS } from "@/consts/academic_year";
  import { PERIODS } from "@/consts/display_period";
  import { ChartMeta } from "@/components/teacher/LineChart.vue";
  import { useLearningStatusYearStore } from "@/store/teacher/learning_status_year_store";

  const learningStatusYearStore = useLearningStatusYearStore()

  // グラフのメタ情報
  const progressRate = ref<ChartMeta>({
    chartId: "progress-rate",
    title: "進捗率",
    unit: "%",
  })
  const studiedQuestions = ref<ChartMeta>({
    chartId: "studied-questions",
    title: "学習した問題数",
    unit: "問",
    extraTooltipLabel: {
      title: "学習を行なった人数",
      unit: "人"
    }
  })
  const studiedTime = ref<ChartMeta>({
    chartId: "studied-time",
    title: "学習時間",
    unit: "分",
    extraTooltipLabel: {
      title: "学習を行なった人数",
      unit: "人"
    }
  })

  const academicSessionId = ref<number|null>(null)
  const academicYear = ref<number|string>('')
  const subject = ref(SUBJECT_ALL.value)
  const period = ref(PERIODS[0].value)
  const workId = ref<number|string>('')

  const conditions = computed(() => {
    return learningStatusYearStore.yearConditions
  })

  const getProgressRateChartData = computed(() => {
    if (!_isSetAcademicYear()) {
      return null
    }
    return learningStatusYearStore.progressRateChartData
  })

  const selectedAcademicSessionId = computed(() => {
    if (!academicSessionId.value) {
      if (conditions.value?.defaultAcademicSessionId) {
        academicSessionId.value = conditions.value.defaultAcademicSessionId
      }
    }
    return academicSessionId.value
  })

  const getStudiedQuestionsChartData = computed(() => {
    if (!_isSetAcademicYear()) {
      return null
    }
    return learningStatusYearStore.studiedQuestionsChartData
  })

  const getStudiedTimeChartData = computed(() => {
    if (!_isSetAcademicYear()) {
      return null
    }
    // 「月間」、「年間」の場合は単位を「時間」にするため、ラベルを更新
    if(period.value == 2 || period.value == 3){
      studiedTime.value["unit"] = "時間"
    }
    else{
      // 「週間」の場合はラベルは「分」
      // 一度「時間」になったラベルの更新用
      studiedTime.value["unit"] = "分"
    }
    return learningStatusYearStore.studiedTimeChartData
  })

  const academicYearsBelongCurrentAcademicSessionId = computed(() => {
    // FIXME: NFR-3801 の対応で当年度固定で設定するように以下の一行を追加、年度を自由に選択できるようにするには削除する
    academicSessionId.value = academicSessionId.value ?? conditions.value?.defaultAcademicSessionId
    return conditions.value && academicSessionId.value ? conditions.value.academicYears[academicSessionId.value] : []
  })

  const classroomsBelongCurrentAcademicYear = computed(() => {
    if (academicSessionId.value in conditions.value.classrooms) {
      if (academicYear.value in conditions.value.classrooms[academicSessionId.value]) {
        return conditions.value.classrooms[academicSessionId.value][academicYear.value]
      }
    }
    return null
  })

  const queryString = computed(() => {
    const queryParams = getQueryParams()
    return Object.entries(queryParams).map(
        (queryParam) => `${queryParam[0]}=${queryParam[1]}`
    ).join("&")
  })

  onMounted(() => {
    // FIXME NFR-7393: fetchLearningStatusYearConditions を await していないため、conditions ロード前に
    //  searchLearningStatus が実行され、academicSessionId が null のまま URL が上書きされる場合がある。
    // 検索条件 (年度・学年・学級一覧) を取得
    learningStatusYearStore.fetchLearningStatusYearConditions()
    // Data properties にクエリパラメーターをセット
    setInitialDataFromSearchParams()
    // グラフデータを取得
    searchLearningStatus()
  })

  function changeAcademicYear() {
    workId.value = ''
    searchLearningStatus()
  }

  function clickSubject(name) {
    subject.value = name
    workId.value = ''
    searchLearningStatus()
  }

  function changeWork() {
    searchLearningStatus()
  }

  function clickPeriod(value) {
    period.value = value
    searchLearningStatus()
  }

  function setInitialDataFromSearchParams() {
    // クエリパラメーターから初期値を設定
    // cf. https://developer.mozilla.org/ja/docs/Web/API/URL/searchParams
    let params = (new URL(location.toString())).searchParams

    if (params.has("academic_session_id")) {
      const parsed = parseInt(params.get("academic_session_id"), 10)
      academicSessionId.value = !isNaN(parsed) ? parsed : null
    }
    if (params.has("academic_year")) {
      const parsed = parseInt(params.get("academic_year"), 10)
      academicYear.value = !isNaN(parsed) ? parsed : ''
    }
    if (params.has("subject")) {
      const found = SUBJECTS.find(subject => subject.value === params.get("subject"))
      subject.value = found ? params.get("subject") : SUBJECT_ALL.value
    }
    if (params.has("period")) {
      const parsed = parseInt(params.get("period"), 10)
      const found = PERIODS.find(period => period.value === parsed)
      period.value = !isNaN(parsed) && found ? parsed : PERIODS[0].value
    }
    if (params.has("work")) {
      const parsed = parseInt(params.get("work"), 10)
      workId.value = !isNaN(parsed) ? parsed : ''
    }
  }

  function getQueryParams() {
    return {
      academic_session_id: academicSessionId.value,
      academic_year: academicYear.value,
      subject: subject.value,
      period: period.value,
      ...(workId.value !== '' && { work: workId.value }),
    }
  }

  function fetchChartData() {
    // グラフデータ取得
    // 最小単位 (特定の1学年✖特定の教科✖特定の期間) でサーバーから取得する
    const payload = {
      academic_session_id: academicSessionId.value,
      academic_year: academicYear.value,
      subject: subject.value,
      period: period.value,
      work: workId.value !== '' ? workId.value : undefined,
    }
    learningStatusYearStore.fetchLearningStatusYearProgressRate(payload)
    learningStatusYearStore.fetchLearningStatusYearStudiedQuestions(payload)
    learningStatusYearStore.fetchLearningStatusYearStudiedTime(payload)
  }

  function updateDocumentTitle() {
    // Document.Title 更新
    const found = SUBJECTS.find(item => item.value === subject.value)
    const subjectName = found ? found.name : SUBJECT_ALL.name
    document.title = `学習状況 ${academicYear.value}年生 ${subjectName} - MANAVIRIA`
  }

  function searchLearningStatus() {
    if (!_isSetAcademicYear()) {
      return
    }
    fetchChartData()

    // アドレスバーのURL, ドキュメント.タイトルにも検索条件を反映する
    // https://developer.mozilla.org/ja/docs/Web/API/History/replaceState
    history.replaceState(getQueryParams(), "", `?${queryString.value}`)
    updateDocumentTitle()
  }

  // グラフ取得用のパラメータがセットされているかチェック
  function _isSetAcademicYear() {
    return JUNIOR_HIGH_ACADEMIC_YEARS.includes(academicYear.value as number)
  }
</script>

<style lang="scss" scoped>
</style>
